'use client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { PAL_INDEX as PALS, type PalIndex as Pal } from "~/lib/pals-index";

type Store = {
  pool: Pal[];
  target?: Pal;
  inPool: (pal: Pal) => boolean;
  togglePool: (pal: Pal) => void;
  addToPool: (pal: Pal) => void;
  removeFromPool: (pal: Pal) => void;
  clearPool: () => void;
  setTarget: (pal?: Pal) => void;
};

const Ctx = createContext<Store | null>(null);
const KEY = "paldeck.breeding.v2"; // v2: Pal ids changed with the Palworld 1.0 data

export function BreedingProvider({ children }: { children: ReactNode }) {
  const [poolIds, setPoolIds] = useState<number[]>([]);
  const [targetId, setTargetId] = useState<number | undefined>();
  const [loaded, setLoaded] = useState(false);

  // restore from browser storage (per-viewer convenience only)
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (raw) {
        const v = JSON.parse(raw) as { pool?: number[]; target?: number };
        if (Array.isArray(v.pool)) setPoolIds(v.pool.filter((n) => typeof n === "number"));
        if (typeof v.target === "number") setTargetId(v.target);
      }
    } catch {
      /* storage unavailable */
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ pool: poolIds, target: targetId }));
    } catch {
      /* storage unavailable */
    }
  }, [poolIds, targetId, loaded]);

  const byId = useMemo(() => new Map(PALS.map((p) => [p.id, p])), []);
  const pool = useMemo(() => poolIds.map((id) => byId.get(id)).filter((p): p is Pal => !!p), [poolIds, byId]);
  const target = targetId === undefined ? undefined : byId.get(targetId);

  const inPool = useCallback((pal: Pal) => poolIds.includes(pal.id), [poolIds]);
  const addToPool = useCallback((pal: Pal) => setPoolIds((ids) => (ids.includes(pal.id) ? ids : [...ids, pal.id])), []);
  const removeFromPool = useCallback((pal: Pal) => setPoolIds((ids) => ids.filter((id) => id !== pal.id)), []);
  const togglePool = useCallback(
    (pal: Pal) => setPoolIds((ids) => (ids.includes(pal.id) ? ids.filter((id) => id !== pal.id) : [...ids, pal.id])),
    [],
  );
  const clearPool = useCallback(() => setPoolIds([]), []);
  const setTarget = useCallback((pal?: Pal) => setTargetId(pal?.id), []);

  const value = useMemo(
    () => ({ pool, target, inPool, togglePool, addToPool, removeFromPool, clearPool, setTarget }),
    [pool, target, inPool, togglePool, addToPool, removeFromPool, clearPool, setTarget],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBreeding(): Store {
  const v = useContext(Ctx);
  if (!v) throw new Error("useBreeding must be used inside BreedingProvider");
  return v;
}
